import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SessionStatusIndicator from '../../components/ui/SessionStatusIndicator';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import ParticipantManagementTab from './components/ParticipantManagementTab';
import PaymentTrackingTab from './components/PaymentTrackingTab';
import SessionSettingsTab from './components/SessionSettingsTab';
import AnnouncementTab from './components/AnnouncementTab';
import ParticipantEditModal from './components/ParticipantEditModal';
import CommentSection from '../public-session-page/components/CommentSection';
import { supabase } from '../../lib/supabaseClient';

const SessionManagementPanel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('id');
  const [activeTab, setActiveTab] = useState('participants');

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [sessionData, setSessionData] = useState(null);
  const [userId, setUserId] = useState('');
  const [profile, setProfile] = useState(null);

  const [membersIndex, setMembersIndex] = useState(new Set());
  const [editParticipantOpen, setEditParticipantOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const buildMemberKey = (name, phone) => {
    return `${String(name || '').trim()}|${String(phone || '').trim()}`;
  };

  const mapParticipantsWithMembers = (list, index) => {
    const idx = index instanceof Set ? index : new Set();
    return (Array.isArray(list) ? list : []).map(p => {
      const key = buildMemberKey(p?.name, p?.phone);
      return {
        ...p,
        isMember: Boolean(p?.isMember) || (key.trim() && idx.has(key))
      };
    });
  };

  const normalizeInviteParticipants = (raw, price) => {
    if (!Array.isArray(raw)) return [];
    const basePrice = parseInt(price) || 0;
    try {
      return raw
        .map(p => {
          const guestCount = parseInt(p?.guestCount ?? p?.guest_count ?? '0') || 0;
          const name = String(p?.name || '').trim();
          const phone = String(p?.phone || '').trim();
          const nickname = String(p?.nickname || p?.name || '').trim();
          const registeredAt = String(p?.registeredAt || p?.registered_at || new Date().toISOString());
          return {
            id: String(p?.id || globalThis.crypto?.randomUUID?.() || String(Date.now())),
            name,
            nickname: nickname || name,
            phone,
            status: String(p?.status || 'pending'),
            payment: String(p?.payment || 'pending'),
            registeredAt,
            skillLevel: String(p?.skillLevel || p?.skill_level || ''),
            guestCount: String(guestCount),
            amount: typeof p?.amount === 'number' ? p.amount : basePrice * (1 + guestCount),
            isMember: Boolean(p?.isMember)
          };
        })
        .filter(p => p.name && p.phone);
    } catch {
      return [];
    }
  };

  const loadMembersIndex = async (uid) => {
    if (!uid) return new Set();
    const { data, error } = await supabase
      .from('members')
      .select('name, phone')
      .eq('client_id', uid);

    if (error) {
      return new Set();
    }

    const idx = new Set();
    (data || []).forEach(r => {
      const key = buildMemberKey(r?.name, r?.phone);
      if (key.trim()) idx.add(key);
    });
    return idx;
  };

  const persistParticipants = async (nextParticipants) => {
    if (!sessionId) return;
    if (!userId) {
      throw new Error('로그인이 필요합니다.');
    }
    const { error } = await supabase
      .from('session_invites')
      .update({
        participants: nextParticipants,
        registered: (nextParticipants || [])?.length || 0
      })
      .eq('id', sessionId)
      .eq('client_id', userId);

    if (error) {
      throw new Error(error?.message || '참가자 정보를 저장하지 못했습니다.');
    }
  };

  const persistSettings = async (updatedSettings) => {
    if (!sessionId) return;
    if (!userId) {
      throw new Error('로그인이 필요합니다.');
    }

    const nextCapacity = Number.isFinite(parseInt(updatedSettings?.capacity))
      ? parseInt(updatedSettings?.capacity)
      : null;
    const nextPrice = Number.isFinite(parseInt(updatedSettings?.pricing?.member))
      ? parseInt(updatedSettings?.pricing?.member)
      : null;

    const patch = {};
    if (typeof nextCapacity === 'number') patch.capacity = nextCapacity;
    if (typeof nextPrice === 'number') patch.price = nextPrice;

    const regDeadlineRaw = String(updatedSettings?.registrationDeadline || '').trim();
    const payDeadlineRaw = String(updatedSettings?.paymentDeadline || '').trim();
    if (regDeadlineRaw) patch.registration_deadline = new Date(regDeadlineRaw).toISOString();
    if (payDeadlineRaw) patch.payment_deadline = new Date(payDeadlineRaw).toISOString();

    if (Object.keys(patch).length === 0) return;

    const { error } = await supabase
      .from('session_invites')
      .update(patch)
      .eq('id', sessionId)
      .eq('client_id', userId);

    if (error) {
      throw new Error(error?.message || '설정 정보를 저장하지 못했습니다.');
    }
  };

  useEffect(() => {
    const loadInvite = async () => {
      if (!sessionId) {
        setLoadError('잘못된 접근입니다. 초대장 ID가 없습니다.');
        return;
      }

      setIsLoading(true);
      setLoadError('');
      try {
        const { data: sessionDataAuth } = await supabase.auth.getSession();
        const uid = sessionDataAuth?.session?.user?.id;
        if (!uid) {
          setLoadError('로그인이 필요합니다.');
          return;
        }
        setUserId(uid);

        const memberIdx = await loadMembersIndex(uid);
        setMembersIndex(memberIdx);

        const { data, error } = await supabase
          .from('session_invites')
          .select('*')
          .eq('id', sessionId)
          .eq('client_id', uid)
          .maybeSingle();

        if (error) {
          setLoadError(error?.message || '초대장 정보를 불러오지 못했습니다.');
          return;
        }

        if (!data) {
          setLoadError('초대장 정보를 찾을 수 없습니다.');
          return;
        }

        const normalizedParticipants = normalizeInviteParticipants(data?.participants, data?.price);
        const withMembers = mapParticipantsWithMembers(normalizedParticipants, memberIdx);

        const toDatetimeLocal = (value) => {
          if (!value) return '';
          try {
            return new Date(value).toISOString().slice(0, 16);
          } catch {
            return '';
          }
        };

        const mapped = {
          id: data?.id,
          token: data?.token,
          title: data?.title,
          date: data?.date ? new Date(data.date).toISOString() : null,
          startTime: data?.start_time,
          endTime: data?.end_time,
          venue: data?.venue,
          capacity: data?.capacity ?? 0,
          status: data?.status,
          registrationDeadline: toDatetimeLocal(data?.registration_deadline) || null,
          paymentDeadline: toDatetimeLocal(data?.payment_deadline) || null,
          allowGuests: true,
          maxGuestsPerMember: 0,
          showRealNames: true,
          autoConfirm: false,
          pricing: {
            member: data?.price ?? 0,
            guest: data?.price ?? 0
          },
          bankInfo: {
            bankName: data?.bank_name || '',
            accountNumber: data?.account_number || '',
            accountHolder: data?.account_holder || ''
          },
          participants: withMembers
        };

        setSessionData(mapped);
      } finally {
        setIsLoading(false);
      }
    };

    loadInvite();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !userId) return;

    const channel = supabase
      .channel(`session-invite-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_invites',
          filter: `id=eq.${sessionId}`
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;

          setSessionData(prev => {
            const normalizedParticipants = normalizeInviteParticipants(row?.participants, row?.price);
            const safeParticipants = normalizedParticipants?.length > 0 ? normalizedParticipants : (prev?.participants || []);
            const withMembers = mapParticipantsWithMembers(safeParticipants, membersIndex);
            return {
              ...(prev || {}),
              id: row?.id,
              token: row?.token,
              title: row?.title,
              date: row?.date ? new Date(row.date).toISOString() : null,
              startTime: row?.start_time,
              endTime: row?.end_time,
              venue: row?.venue,
              capacity: row?.capacity ?? 0,
              status: row?.status,
              pricing: {
                ...(prev?.pricing || {}),
                member: row?.price ?? (prev?.pricing?.member ?? 0),
                guest: row?.price ?? (prev?.pricing?.guest ?? 0)
              },
              participants: withMembers
            };
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, userId, membersIndex]);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    const loadProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name, phone')
          .eq('id', userId)
          .maybeSingle();
        if (!mounted) return;
        if (error) {
          setProfile(null);
          return;
        }
        setProfile(data || null);
      } catch {
        if (mounted) setProfile(null);
      }
    };
    loadProfile();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const myAttendance = useMemo(() => {
    if (!profile || !sessionData?.participants) return null;
    const name = String(profile?.name || '').trim();
    const phone = String(profile?.phone || '').trim();
    return (sessionData.participants || []).find((p) => {
      if (!p) return false;
      const pName = String(p?.name || p?.nickname || '').trim();
      const pPhone = String(p?.phone || '').trim();
      if (phone) return pPhone === phone && (!name || pName === name);
      return name && pName === name;
    }) || null;
  }, [profile, sessionData?.participants]);

  const tabs = [
    { id: 'participants', label: '참가자 관리', icon: 'Users' },
    { id: 'payment', label: '결제 관리', icon: 'DollarSign' },
    { id: 'settings', label: '설정', icon: 'Settings' },
    { id: 'announcement', label: '공지 생성', icon: 'MessageSquare' },
    { id: 'inquiry', label: '문의', icon: 'MessageCircle' }
  ];

  const handleEditParticipant = (participant) => {
    setSelectedParticipant(participant || null);
    setEditParticipantOpen(true);
  };

  const handleSaveParticipantFromModal = async (updatedParticipant) => {
    if (!userId) throw new Error('로그인이 필요합니다.');

    const name = String(updatedParticipant?.name || '').trim();
    const phone = String(updatedParticipant?.phone || '').trim();

    if (updatedParticipant?.isMember) {
      const { data: existing, error: existingError } = await supabase
        .from('members')
        .select('id')
        .eq('client_id', userId)
        .eq('name', name)
        .eq('phone', phone)
        .maybeSingle();

      if (existingError) {
        throw new Error(existingError?.message || '회원 중복 확인에 실패했습니다.');
      }

      if (existing?.id) {
        alert('이미 등록되어 있습니다.');
      } else {
        const skillLevel = String(updatedParticipant?.skillLevel || '').trim() || '1.0';
        const { error: insertError } = await supabase
          .from('members')
          .insert({
            client_id: userId,
            name,
            phone,
            skill_level: skillLevel,
            membership_type: 'regular',
            status: 'active'
          });

        if (insertError) {
          throw new Error(insertError?.message || '회원 등록에 실패했습니다.');
        }

        const nextIdx = new Set(membersIndex);
        nextIdx.add(buildMemberKey(name, phone));
        setMembersIndex(nextIdx);
      }
    }

    await handleUpdateParticipant(updatedParticipant);
  };

  const handleBulkUpdateParticipants = async (nextParticipants) => {
    if (!userId) {
      alert('로그인이 필요합니다.');
      return;
    }

    const prevParticipantsSnapshot = sessionData?.participants || [];
    const base = Array.isArray(nextParticipants) ? nextParticipants : [];
    if (prevParticipantsSnapshot.length > 0 && base.length === 0) {
      alert('참가자 목록이 비어있어 저장할 수 없습니다.');
      return;
    }

    const withMembers = mapParticipantsWithMembers(base, membersIndex);
    setSessionData(prev => ({
      ...(prev || {}),
      participants: withMembers
    }));

    try {
      await persistParticipants(withMembers);
    } catch (e) {
      setSessionData(prev => ({
        ...(prev || {}),
        participants: prevParticipantsSnapshot
      }));
      alert(e?.message || '저장에 실패했습니다.');
    }
  };

  const handleDownloadRoster = () => {
    const list = Array.isArray(sessionData?.participants) ? sessionData.participants : [];
    if (list.length === 0) {
      alert('다운로드할 참가자가 없습니다.');
      return;
    }

    const escapeHtml = (s) => {
      return String(s ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    };

    const rows = list
      .map((p, idx) => {
        const statusLabel = p?.status === 'confirmed' ? '확정' : p?.status === 'pending' ? '대기' : p?.status || '';
        const paymentLabel = p?.payment === 'paid' ? '완료' : p?.payment === 'pending' ? '대기' : p?.payment || '';
        const memberLabel = p?.isMember ? '회원' : '게스트';
        return `<tr>` +
          `<td>${idx + 1}</td>` +
          `<td>${escapeHtml(p?.name)}</td>` +
          `<td>${escapeHtml(p?.phone)}</td>` +
          `<td>${escapeHtml(memberLabel)}</td>` +
          `<td>${escapeHtml(statusLabel)}</td>` +
          `<td>${escapeHtml(paymentLabel)}</td>` +
          `<td>${escapeHtml(p?.registeredAt)}</td>` +
        `</tr>`;
      })
      .join('');

    const html = `\ufeff<html><head><meta charset="utf-8" /></head><body>` +
      `<table border="1">` +
      `<thead><tr>` +
      `<th>No</th><th>이름</th><th>전화번호</th><th>회원</th><th>상태</th><th>결제</th><th>신청일</th>` +
      `</tr></thead>` +
      `<tbody>${rows}</tbody>` +
      `</table>` +
      `</body></html>`;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const datePart = sessionData?.date ? String(sessionData.date).slice(0, 10) : '';
    const titlePart = String(sessionData?.title || 'roster').replaceAll(' ', '_');
    a.href = url;
    a.download = `${titlePart}_${datePart || 'session'}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleUpdateParticipant = async (updatedParticipant) => {
    if (!userId) {
      alert('로그인이 필요합니다.');
      return;
    }
    const prevParticipantsSnapshot = sessionData?.participants || [];
    if (prevParticipantsSnapshot.length === 0) {
      alert('참가자 목록을 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const exists = prevParticipantsSnapshot.some(p => p?.id === updatedParticipant?.id);
    if (!exists) {
      alert('업데이트할 참가자를 찾지 못했습니다. 새로고침 후 다시 시도해주세요.');
      return;
    }

    const nextParticipants = prevParticipantsSnapshot.map(p =>
      p?.id === updatedParticipant?.id ? updatedParticipant : p
    );

    setSessionData(prev => ({
      ...(prev || {}),
      participants: nextParticipants
    }));

    try {
      await persistParticipants(nextParticipants);
    } catch (e) {
      setSessionData(prev => ({
        ...(prev || {}),
        participants: prevParticipantsSnapshot
      }));
      alert(e?.message || '저장에 실패했습니다.');
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!userId) {
      alert('로그인이 필요합니다.');
      return;
    }
    const prevParticipantsSnapshot = sessionData?.participants || [];
    if (prevParticipantsSnapshot.length === 0) {
      alert('참가자 목록을 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const exists = prevParticipantsSnapshot.some(p => p?.id === participantId);
    if (!exists) {
      alert('삭제할 참가자를 찾지 못했습니다. 새로고침 후 다시 시도해주세요.');
      return;
    }

    const nextParticipants = prevParticipantsSnapshot.filter(p => p?.id !== participantId);
    setSessionData(prev => ({
      ...(prev || {}),
      participants: nextParticipants
    }));

    try {
      await persistParticipants(nextParticipants);
    } catch (e) {
      setSessionData(prev => ({
        ...(prev || {}),
        participants: prevParticipantsSnapshot
      }));
      alert(e?.message || '저장에 실패했습니다.');
    }
  };

  const handleUpdatePayment = async (updatedParticipant) => {
    await handleUpdateParticipant(updatedParticipant);
  };

  const handleGenerateBracket = (bracket) => {
    console.log('Generated bracket:', bracket);
  };

  const handleUpdateSettings = async (updatedSettings) => {
    const prevSnapshot = sessionData;
    setSessionData(prev => ({
      ...prev,
      ...updatedSettings
    }));

    try {
      await persistSettings(updatedSettings);
    } catch (e) {
      setSessionData(prevSnapshot);
      alert(e?.message || '저장에 실패했습니다.');
      throw e;
    }
  };

  const handleBackToList = () => {
    navigate('/session-list-dashboard/invite');
  };

  const handleShareSession = () => {
    const token = sessionData?.token;
    if (!token) {
      alert('공유할 토큰이 없습니다.');
      return;
    }
    const url = `${window.location.origin}/public-session-page?token=${token}`;
    navigator.clipboard?.writeText(url);
    alert('초대장 링크가 클립보드에 복사되었습니다.');
  };

  const publicUrl = useMemo(() => {
    const token = sessionData?.token;
    if (!token) return '';
    return `${window.location.origin}/public-session-page?token=${token}`;
  }, [sessionData?.token]);

  const registeredCount = sessionData?.participants?.length || 0;
  const confirmedCount = sessionData?.participants?.filter(p => p?.status === 'confirmed')?.length || 0;

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBackToList}
              className="mb-4"
            >
              초대장 목록으로
            </Button>

            {isLoading && (
              <div className="bg-muted border border-border rounded-xl p-6">
                <p className="text-sm text-muted-foreground">초대장 정보를 불러오는 중...</p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="bg-error/10 border border-error/20 rounded-xl p-6">
                <p className="text-sm text-error">{loadError}</p>
              </div>
            )}

            {!isLoading && !loadError && !sessionData && (
              <div className="bg-muted border border-border rounded-xl p-6">
                <p className="text-sm text-muted-foreground">초대장 정보가 없습니다.</p>
              </div>
            )}

            {!isLoading && !loadError && sessionData && (
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 md:gap-6">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  {sessionData?.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm md:text-base text-muted-foreground mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} />
                    <span>
                      {new Date(sessionData.date)?.toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'long'
                      })}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} />
                    <span>{sessionData?.startTime} - {sessionData?.endTime}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} />
                    <span>{sessionData?.venue}</span>
                  </div>
                </div>

                <SessionStatusIndicator
                  status={sessionData?.status}
                  capacity={sessionData?.capacity}
                  registered={registeredCount}
                  showTooltip={true}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="default"
                  iconName="Share2"
                  iconPosition="left"
                  onClick={handleShareSession}
                >
                  공유
                </Button>
                
                <Button
                  variant="outline"
                  size="default"
                  iconName="ExternalLink"
                  iconPosition="left"
                  onClick={() => {
                    if (!publicUrl) {
                      alert('공개 페이지 링크를 만들 수 없습니다.');
                      return;
                    }
                    window.open(publicUrl, '_blank');
                  }}
                >
                  공개 페이지
                </Button>
              </div>
            </div>
            )}
          </div>

          {!isLoading && !loadError && sessionData && (
            <>
              <div className="mb-6">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base md:text-lg font-semibold text-foreground">내 참석 정보</h2>
                    <Icon name="UserCheck" size={18} className="text-primary" />
                  </div>
                  {myAttendance ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Icon name="User" size={14} />
                        <span className="text-foreground font-medium">
                          {myAttendance.nickname || myAttendance.name || '참가자'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Phone" size={14} />
                        <span>{myAttendance.phone || '연락처 미등록'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={14} />
                        <span>상태: {myAttendance.status || 'pending'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="DollarSign" size={14} />
                        <span>결제: {myAttendance.payment || 'pending'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Users" size={14} />
                        <span>동반: {myAttendance.guestCount || '0'}명</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={14} />
                        <span>신청일: {myAttendance.registeredAt || '-'}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">현재 참석 정보가 없습니다.</p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <div className="hidden lg:flex gap-2 border-b border-border">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        flex items-center gap-2 px-4 py-3 border-b-2 transition-athletic
                        ${activeTab === tab?.id
                          ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                        }
                      `}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>

                <div className="lg:hidden">
                  <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e?.target?.value)}
                    className="w-full px-4 py-3 bg-card border border-border rounded-md text-foreground"
                  >
                    {tabs?.map((tab) => (
                      <option key={tab?.id} value={tab?.id}>
                        {tab?.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-8">
                {activeTab === 'participants' && (
                  <ParticipantManagementTab
                    sessionData={sessionData}
                    onUpdateParticipant={handleUpdateParticipant}
                    onDeleteParticipant={handleDeleteParticipant}
                    onEditParticipant={handleEditParticipant}
                    onBulkUpdateParticipants={handleBulkUpdateParticipants}
                    onDownloadRoster={handleDownloadRoster}
                  />
                )}

                {activeTab === 'payment' && (
                  <PaymentTrackingTab
                    sessionData={sessionData}
                    onUpdatePayment={handleUpdatePayment}
                  />
                )}

                {activeTab === 'settings' && (
                  <SessionSettingsTab
                    sessionData={sessionData}
                    onUpdateSettings={handleUpdateSettings}
                  />
                )}

                {activeTab === 'announcement' && (
                  <AnnouncementTab sessionData={sessionData} />
                )}

                {activeTab === 'inquiry' && sessionId && (
                  <CommentSection sessionInviteId={sessionId} isAdmin={true} />
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <ParticipantEditModal
        isOpen={editParticipantOpen}
        onClose={() => {
          setEditParticipantOpen(false);
          setSelectedParticipant(null);
        }}
        participant={selectedParticipant}
        onSave={handleSaveParticipantFromModal}
      />
    </OpenPlayToolsLayout>
  );
};

export default SessionManagementPanel;